/**
 * GapsSection
 * 
 * Search result section for Gaps (EX6)
 * Displays matching gaps in a table
 * Matches exact styling from GapsPage.tsx
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { Gap } from '../../../../services/gapService';
import HighlightText from '../components/HighlightText';

// ========================================
// Styled Components - Match GapsPage.tsx exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #FEE2E2;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #FECACA;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #991B1B;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    white-space: nowrap;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 12px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

// Chip Components - Match GapsPage.tsx exactly
const StatusChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status) {
      case 'Open': return '#4AB7B6';
      case 'Draft': return '#E5B454';
      case 'Archived': return '#9CA3AF';
      default: return '#F3F4F6';
    }
  }};
  color: white;
  
  &::before {
    content: '●';
    font-size: 8px;
  }
`;

const GapIdChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CycleChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RelatedObjectsChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
  background: white;
  white-space: nowrap;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 8px;
  }
`;

const GapTypeChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #FEF9C3;
  color: #A16207;
  white-space: nowrap;
`;

const GapOwnerText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1F2937;
`;

const ProcessOwnerContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #F8FAFC;
  border-radius: 50px;
  white-space: nowrap;
`;

const ProcessOwnerAvatar = styled.span`
  width: 14px;
  min-width: 14px;
  height: 14px;
  min-height: 14px;
  border-radius: 50%;
  background: #F97316;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 7px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ProcessOwnerName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompensatingControlChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const TruncatedText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  cursor: pointer;
  color: #2563EB;
  line-height: 1.4;
  font-size: 12px;
`;

const DateText = styled.span`
  font-size: 12px;
  color: #1F2937;
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
  font-size: 12px;
`;

// Tooltip components
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #1F2937;
  margin-bottom: 8px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TooltipItem = styled.div`
  padding: 4px 8px;
  background: #EFF6FF;
  border-radius: 4px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: #2563EB;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: #DBEAFE;
  }
`;

// ========================================
// Types
// ========================================

export interface GapsSectionProps {
  data: Gap[];
  searchText: string;
}

// ========================================
// Component
// ========================================

const GapsSection: React.FC<GapsSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToGap = (gapId: string) => {
    navigate(`/gaps/${gapId}`);
  };

  const handleNavigateToCycle = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleNavigateToProcess = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  const handleNavigateToControl = (controlId: string) => {
    navigate(`/control-activities/${controlId}`);
  };

  const handleNavigateToObjective = (objectiveCode: string) => {
    const processCode = objectiveCode.split('.')[0];
    navigate(`/risk-assessment/${processCode}/tab2`);
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // Table columns - matching GapsPage.tsx
  const columns: ColumnsType<Gap> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string, record) => (
        <StatusChip 
          $status={status} 
          onClick={() => handleNavigateToGap(record.id)}
          style={{ cursor: 'pointer' }}
        >
          {status}
        </StatusChip>
      ),
    },
    {
      title: 'GAP ID',
      dataIndex: 'gapId',
      key: 'gapId',
      width: 90,
      render: (gapId: string, record) => (
        <GapIdChip onClick={() => handleNavigateToGap(record.id)}>
          <HighlightText 
            text={record.status === 'Draft' || !gapId ? 'Pending' : gapId} 
            searchText={searchText} 
          />
        </GapIdChip>
      ),
    },
    {
      title: 'Cycle',
      dataIndex: 'cycle',
      key: 'cycle',
      width: 130,
      render: (cycle: Gap['cycle']) => {
        const cycleText = `${cycle?.code || ''} ${cycle?.name || ''}`.trim();
        return (
          <Tooltip title={cycleText}>
            <CycleChip onClick={() => handleNavigateToCycle(cycle?.id || '')}>
              <HighlightText text={cycleText || '-'} searchText={searchText} />
            </CycleChip>
          </Tooltip>
        );
      },
    },
    {
      title: 'Process',
      dataIndex: 'linkedProcess',
      key: 'linkedProcess',
      width: 130,
      render: (process: Gap['linkedProcess']) => {
        const processText = `${process?.code || ''} ${process?.name || ''}`.trim();
        return (
          <Tooltip title={processText}>
            <ProcessChip onClick={() => handleNavigateToProcess(process?.id || '')}>
              <HighlightText text={processText || '-'} searchText={searchText} />
            </ProcessChip>
          </Tooltip>
        );
      },
    },
    {
      title: 'Related Objectives',
      dataIndex: 'relatedObjectives',
      key: 'relatedObjectives',
      width: 140,
      render: (objectives: Gap['relatedObjectives']) => {
        if (!objectives || objectives.length === 0) {
          return <EmptyCell>-</EmptyCell>;
        }

        const tooltipContent = (
          <div>
            <TooltipTitle>Related Objectives</TooltipTitle>
            <TooltipList>
              {objectives.map((obj) => (
                <TooltipItem
                  key={obj.id}
                  onClick={() => handleNavigateToObjective(obj.code)}
                >
                  {obj.code}
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '300px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '12px',
              borderRadius: '8px',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
            }}
            color="#FFFFFF"
          >
            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
              {objectives.slice(0, 2).map((obj) => (
                <RelatedObjectsChip key={obj.id} onClick={() => handleNavigateToObjective(obj.code)}>
                  {obj.code}
                </RelatedObjectsChip>
              ))}
              {objectives.length > 2 && (
                <RelatedObjectsChip>+{objectives.length - 2}</RelatedObjectsChip>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Related Controls',
      dataIndex: 'effectiveControl',
      key: 'effectiveControl',
      width: 120,
      render: (effectiveControl: Gap['effectiveControl']) => {
        if (!effectiveControl || effectiveControl.code === '-') {
          return <EmptyCell>-</EmptyCell>;
        }
        return (
          <CompensatingControlChip onClick={() => handleNavigateToControl(effectiveControl.id)}>
            {effectiveControl.code}
          </CompensatingControlChip>
        );
      },
    },
    {
      title: 'GAP Description',
      dataIndex: 'gapDescription',
      key: 'gapDescription',
      width: 150,
      render: (description: string) => (
        description ? (
          <Tooltip title={description}>
            <TruncatedText>
              <HighlightText text={description} searchText={searchText} />
            </TruncatedText>
          </Tooltip>
        ) : <EmptyCell>-</EmptyCell>
      ),
    },
    {
      title: 'Risk Description',
      dataIndex: 'riskDescription',
      key: 'riskDescription',
      width: 150,
      render: (riskDescription: string) => (
        riskDescription ? (
          <Tooltip title={riskDescription}>
            <TruncatedText>
              <HighlightText text={riskDescription} searchText={searchText} />
            </TruncatedText>
          </Tooltip>
        ) : <EmptyCell>-</EmptyCell>
      ),
    },
    {
      title: 'Risk Ranking',
      dataIndex: 'gapRiskRanking',
      key: 'gapRiskRanking',
      width: 110,
      render: (ranking: string) => (
        <RiskRankingChip $level={ranking}>
          {ranking || 'Unranked'}
        </RiskRankingChip>
      ),
    },
    {
      title: 'GAP Type',
      dataIndex: 'gapType',
      key: 'gapType',
      width: 100,
      render: (type: string | null) => (
        type ? <GapTypeChip>{type}</GapTypeChip> : <EmptyCell>-</EmptyCell>
      ),
    },
    {
      title: 'GAP Owner',
      dataIndex: 'gapOwner',
      key: 'gapOwner',
      width: 120,
      render: (owner: string) => (
        <GapOwnerText>
          <HighlightText text={owner || '-'} searchText={searchText} />
        </GapOwnerText>
      ),
    },
    {
      title: 'Process Owner',
      dataIndex: 'processOwner',
      key: 'processOwner',
      width: 220,
      render: (owner: Gap['processOwner']) => {
        if (!owner || !owner.name) {
          return <EmptyCell>Unknown</EmptyCell>;
        }
        return (
          <ProcessOwnerContainer>
            <ProcessOwnerAvatar>
              {owner.avatar || owner.name.charAt(0)}
            </ProcessOwnerAvatar>
            <ProcessOwnerName>{owner.name}</ProcessOwnerName>
          </ProcessOwnerContainer>
        );
      },
    },
    {
      title: 'Compensating Controls',
      dataIndex: 'relatedControls',
      key: 'compensatingControls',
      width: 150,
      render: (controls: Gap['relatedControls']) => {
        if (!controls || controls.length === 0) {
          return <EmptyCell>-</EmptyCell>;
        }

        const tooltipContent = controls.length > 1 ? (
          <div>
            <TooltipTitle>Compensating Controls</TooltipTitle>
            <TooltipList>
              {controls.map((ctrl) => (
                <TooltipItem 
                  key={ctrl.id}
                  onClick={() => handleNavigateToControl(ctrl.id)}
                >
                  {ctrl.code}
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        ) : null;

        const chipContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {controls.slice(0, 2).map((ctrl) => (
              <CompensatingControlChip key={ctrl.id} onClick={() => handleNavigateToControl(ctrl.id)}>
                {ctrl.code}
              </CompensatingControlChip>
            ))}
            {controls.length > 2 && (
              <CompensatingControlChip>+{controls.length - 2}</CompensatingControlChip>
            )}
          </div>
        );

        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '300px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
              }}
              color="#FFFFFF"
            >
              {chipContent}
            </Tooltip>
          );
        }

        return chipContent;
      },
    },
    {
      title: 'Management Priority',
      dataIndex: 'managementPrioritization',
      key: 'managementPrioritization',
      width: 130,
      render: (priority: string | null) => (
        priority ? (
          <RiskRankingChip $level={priority}>
            {priority}
          </RiskRankingChip>
        ) : <EmptyCell>-</EmptyCell>
      ),
    },
    {
      title: 'Date Identified',
      dataIndex: 'dateIdentified',
      key: 'dateIdentified',
      width: 100,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
  ];

  // Don't render if no results
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>ICFR GAP Tracker ({data.length} results)</SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <TableWrapper>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 1800 }}
          />
        </TableWrapper>
      </ContentContainer>
    </SectionContainer>
  );
};

export default GapsSection;
